import styled from "@emotion/styled";
import { map, slice } from "lodash-es";
import { useEffect, useState } from "react";

import {
  MDSButton,
  MDSButtonVariant,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { AgentSection } from "@/pages/agent/components/agent-section";
import { AgentProjectCard } from "@/pages/agent/projects/agent-project-card";
import {
  ProjectSectionKind,
  type ProjectCardViewModel,
} from "@/pages/agent/projects/agent-projects-page.utils";

export const AgentProjectsSection = ({
  title,
  kind,
  dataLoaded,
  projectCards,
  queryKey,
  onOpenActivities,
  onOpenProject,
  onOpenProjectTasks,
  onOpenTask,
}: {
  title: string;
  kind: ProjectSectionKind;
  dataLoaded: boolean;
  projectCards: ProjectCardViewModel[];
  queryKey: string;
  onOpenActivities: ({ projectId }: { projectId: string }) => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenProjectTasks: ({ projectId }: { projectId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
}) => {
  const [visibleProjectCount, setVisibleProjectCount] = useState(AGENT_PROJECT_PAGE_SIZE);
  const visibleProjectCards = slice(projectCards, 0, visibleProjectCount);

  useEffect(() => {
    setVisibleProjectCount(AGENT_PROJECT_PAGE_SIZE);
  }, [queryKey]);

  return (
    <AgentSection title={title}>
      {!dataLoaded && projectCards.length === 0 ? (
        <StyledLoadingState aria-label={`Loading ${title.toLowerCase()}`}>
          <MDSSkeleton height={18} width="40%" />
          <MDSSkeleton height={14} width="70%" />
        </StyledLoadingState>
      ) : projectCards.length === 0 ? (
        <StyledEmptyState>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            {`No ${title.toLowerCase()}`}
          </MDSText>
        </StyledEmptyState>
      ) : (
        <>
          <StyledProjectGrid>
            {map(visibleProjectCards, projectCard => (
              <AgentProjectCard
                key={projectCard.project.id}
                kind={kind}
                projectCard={projectCard}
                onOpenActivities={onOpenActivities}
                onOpenProject={onOpenProject}
                onOpenProjectTasks={onOpenProjectTasks}
                onOpenTask={onOpenTask}
              />
            ))}
          </StyledProjectGrid>
          {visibleProjectCards.length < projectCards.length ? (
            <StyledLoadMoreButton
              variant={MDSButtonVariant.Transparent}
              onPress={() => {
                setVisibleProjectCount(currentCount =>
                  Math.min(currentCount + AGENT_PROJECT_PAGE_SIZE, projectCards.length)
                );
              }}
            >
              Show more projects
            </StyledLoadMoreButton>
          ) : null}
        </>
      )}
    </AgentSection>
  );
};

const AGENT_PROJECT_PAGE_SIZE = 20;

const StyledProjectGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[5]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StyledLoadMoreButton = styled(MDSButton)`
  align-self: center;
`;

const StyledEmptyState = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[5]};
`;

const StyledLoadingState = styled(StyledEmptyState)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;
